import _ from 'lodash';
import moment from 'moment';
import constants from '$$helpers/constants';
import estimationsModule from '../../../components/estimations';
import projectsModel from '$$projectModel';
import Store from '$$globalStore';
import customDayTemplatesModel from '$$customDayTemplatesModel';
import timeTrackingModel from '$$timeTrackingModel';
import app from '$$app';
import reportHelpers from '../include/helpers';
import globalToast from '$$vueCmp/toast';
import { ColorCycler } from '../include/helpers';

class BudgetByResourceReport {
  constructor() {
    this.costWorker = null;
    this.reportTasks = {};
    this.calculationMode = null;

    this.projectTasksByResourceMap = new Map();
    this.projectResourcesMap = new Map();
    this.projectResourcesByIdMap = new Map();
    this.tasksCostsMap = new Map();
    this.resourcesLogsMap = new Map();
    this.projectDataMap = new Map()
    
    this.workerSettings = {};
  }

  init() {
    const allProjectsData = Store.getters['tasksModel/getNotArchivedData'];
    
    allProjectsData.forEach(projectTasksData => this.createCacheByProjectTasksData(projectTasksData));

    this.initCostWorker();
    this.initWorkerSettings();
  }

  createCacheByProjectTasksData(projectTasksData) {
    const { id: ganttId, resourcesToTasks, tasks } = projectTasksData;
    const projectResources = Store.getters['resourcesModel/getResourcesByProjectId'](ganttId);
    const projectDataFromModel = projectsModel.getProjectDataById(ganttId);

    const resourcesTasks = [];
    const resourcesLogsMap = {}
    const tasksCostsMap = {};

    projectResources.forEach(res => {
      resourcesLogsMap[res.id] = timeTrackingModel.getLogsByResourceId(res.id);
    });

    Object.values(resourcesToTasks).forEach(tasks => {
      if (!tasks?.length) return;
      resourcesTasks.push(...tasks);
    });

    tasks.forEach(task => {
      tasksCostsMap[task.id] = {
        cost: estimationsModule.calculateCostChildTask(task) || 0,
        actual_cost: estimationsModule.calculateActualCostChildTask(task) || 0,
      }
    })

    const groupedResourcesTasks = _.groupBy(resourcesTasks, 'resource_id');
    this.projectTasksByResourceMap.set(ganttId, groupedResourcesTasks);
    this.projectResourcesMap.set(ganttId, projectResources);
    this.projectResourcesByIdMap.set(ganttId, Object.fromEntries(projectResources.map(resource => [resource.id, resource])));
    this.tasksCostsMap.set(ganttId, tasksCostsMap);
    this.resourcesLogsMap.set(ganttId, resourcesLogsMap);
    this.projectDataMap.set(ganttId, projectDataFromModel);
  }

  destroy() {
    this.projectTasksByResourceMap.clear()
    this.tasksCostsMap.clear();
    this.resourcesLogsMap.clear();
    this.projectResourcesMap.clear();
    this.projectResourcesByIdMap.clear();
    this.projectDataMap.clear();
  }

  initWorkerSettings() {
    const allResourcesData = Store.getters['resourcesModel/getAllResources'];
    const projectSettingsMap = {};

    projectsModel.serialize().forEach(settings => {
      projectSettingsMap[settings.gantt_id] = {
        ...settings,
        totalEstimate: Store.getters['tasksModel/getTotalEstimateDataForProject'](settings.gantt_id),
      };
    });

    this.workerSettings.projectConfigs = reportHelpers.prepareProjectConfigs();
    this.workerSettings.projectSettings = projectSettingsMap;
    this.workerSettings.customDaysData = customDayTemplatesModel.serialize();
    this.workerSettings.resourcesData = allResourcesData;
  }

  initCostWorker() {
    if (window.Worker && !this.costWorker) {
      let version = Date.now();

      if (GT.productVersion && GT.productVersion.buildVersion) {
        version = GT.productVersion.buildVersion;
      }

      this.costWorker = new Worker('/workers/costs_worker.js' + `?${version}`);
      this._initEvents();
    }
  }

  _initEvents() {
    const that = this;

    this.costWorker.onmessage = function (e) {
      const updatedCosts = e.data;

      Object.entries(updatedCosts).forEach(([key, updates]) => {
        if (updates.length) {
          const tasks = that.reportTasks[key]?.tasks;

          if (Array.isArray(tasks)) {
            const updatesMap = new Map(updates.map(obj => [obj.id, obj]));

            that.reportTasks[key].tasks = tasks.map(task => {
              const update = updatesMap.get(task.id);

              if (update) {
                return {
                  ...task,
                  cost: update.cost,
                  actual_cost: update.actual_cost,
                };
              }

              return task;
            });
          }
        }
      });

      that.initReportData();
    };

    this.costWorker.onerror = function (e) {
      globalToast.error(__('costs_calculating_error'));
    };
  }

  calculateCostsByWorker(projectIds, baselineTasks, calculationMode) {
    this.calculationMode = calculationMode;
    this.reportTasks = {};

    const currentFilter = Store.getters['filter/currentFilter'];
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;
    const filterRange = startDate && endDate ? { start: startDate, end: endDate } : null;
    const tasksMap = Store.getters['tasksModel/tasksByIds'];

    const baselineTasksCostsMap = new Map();

    if (projectIds?.length) {
      if (baselineTasks?.length) {
        baselineTasksCostsMap.clear();
        baselineTasks.forEach(task => {
          const baselineCosts = {
            cost: estimationsModule.calculateCostChildTask(task) || 0,
            actual_cost: estimationsModule.calculateActualCostChildTask(task) || 0,
          }

          baselineTasksCostsMap.set(task.id, baselineCosts)
        })
      }

      projectIds.forEach((projectId, index) => {
        if (!this.projectDataMap.has(projectId)) {
          const projectTasksDataFromStore = Store.getters['tasksModel/getItem'](projectId);

          if (projectTasksDataFromStore) {
            this.createCacheByProjectTasksData(projectTasksDataFromStore);
          }
        }
        const projectResources = this.projectResourcesMap.get(projectId);
        const totalEstimateData = Store.getters['tasksModel/getTotalEstimateDataForProject'](projectId);
        const rangeForWorker = {
          start: currentFilter?.properties?.rangeDate?.start || totalEstimateData.start_date,
          end: currentFilter?.properties?.rangeDate?.end || totalEstimateData.end_date,
        };

        if (projectResources?.length) {
          projectResources.forEach(res => {
            const tasksByResource = this.projectTasksByResourceMap.get(projectId)[res.id];
            const preparedTasksForWorker = this.prepareTasksForWorker(tasksByResource, tasksMap, projectId, baselineTasksCostsMap);

            const filteredTasks = preparedTasksForWorker.filter(task => reportHelpers.returnTaskByRange({
              ...task,
              start_date: task.task_data.start_date,
              end_date: task.task_data.end_date,
            }, filterRange));

            

            this.reportTasks[`resourceId:${res.id}-projectId:${projectId}`] = { tasks: filteredTasks, range: rangeForWorker };
          });
        }
      });

      if (baselineTasks?.length) {
        this.prepareBaselineTasksForWorker(baselineTasks, filterRange);
      }
    }

    this.sendDataToWorker(this.reportTasks);
  }

  prepareBaselineTasksForWorker(baselineTasks, filterRange) {
    const currentFilter = Store.getters['filter/currentFilter'];
    const baselineTotalEstimate = baselineTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
    const baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
    const rangeForWorker = {
      start: currentFilter?.properties?.rangeDate?.start || baselineTotalEstimate?.start_date || moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: currentFilter?.properties?.rangeDate?.end || baselineTotalEstimate?.end_date || moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    };

    baselineTasksAndMilestones.forEach(task => {
      if (task.resources && task.resources.length) {
        task.resources.forEach(res => {
          const filteredTask = reportHelpers.returnTaskByRange(task, filterRange);

          if (filteredTask) {
            // const resource = Store.getters['resourcesModel/getResourceById'](res.resource_id);
            const resource = this.projectResourcesByIdMap.get(baselineTotalEstimate.gantt_id)[res.resource_id];
            const preparedBaselineTaskForWorker = this.prepareBaselineTaskForWorker(task, resource);

            this.reportTasks[`baseline-${res.resource_id}-${task.id}`] = { tasks: [preparedBaselineTaskForWorker], range: rangeForWorker };
          }
        });
      }
    });
  }

  sendDataToWorker(tasksData) {
    this.costWorker.postMessage(_.cloneDeep({
      projectConfigs: this.workerSettings.projectConfigs,
      projectSettings: this.workerSettings.projectSettings,
      customDaysData: this.workerSettings.customDaysData,
      resourcesData: this.workerSettings.resourcesData,
      tasksData,
    }));
  }

  prepareTasksForWorker(tasksByResource, tasksMap, projectId, baselineCostsMap) {
    const resultData = [];

    _.each(tasksByResource, resourceTaskData => {
      const originalTaskData = tasksMap[resourceTaskData.task_id];
      // const resource = Store.getters['resourcesModel/getResourceById'](resourceTaskData.resource_id);
      const resource = this.projectResourcesByIdMap.get(projectId)[resourceTaskData.resource_id];
      const logsData = this.resourcesLogsMap.get(originalTaskData.gantt_id)[resourceTaskData.resource_id];
      const taskLogsData = logsData?.length ? logsData.filter(log => log.task_id === resourceTaskData.task_id) : [];

      let cost;
      let actual_cost;

      if (baselineCostsMap && baselineCostsMap.size > 0) {
        cost = baselineCostsMap.get(originalTaskData.id)?.cost || 0;
        actual_cost = baselineCostsMap.get(originalTaskData.id)?.actual_cost || 0;
      } else {
        const taskCosts = this.tasksCostsMap.get(originalTaskData.gantt_id)?.[originalTaskData.id];

        if (taskCosts === undefined) {
          return;
        }

        cost = taskCosts.cost;
        actual_cost = taskCosts.actual_cost;
      }

      resultData.push({
        id: originalTaskData.id,
        gantt_id: originalTaskData.gantt_id,
        task_id: originalTaskData.id, // do not remove
        task_data: originalTaskData,
        resources: [resource],
        resource_id: String(resourceTaskData.resource_id),
        resource_data: [resource],
        // cost: estimationsModule.calculateCostChildTask(originalTaskData) || 0,
        // actual_cost: estimationsModule.calculateActualCostChildTask(originalTaskData) || 0,
        cost,
        actual_cost,
        cost_range_start: originalTaskData.start_date,
        cost_range_end: originalTaskData.end_date,
        logsData: taskLogsData,
      });
    });

    return resultData;
  }

  prepareBaselineTaskForWorker(baselineTask, resource) {
    const logsData = this.resourcesLogsMap.get(baselineTask.gantt_id)[resource.id];
    const taskLogsData = logsData?.length ? logsData.filter(log => log.task_id === baselineTask.id) : [];

    return {
      id: baselineTask.id,
      gantt_id: baselineTask.gantt_id,
      task_id: baselineTask.id, // do not remove
      task_data: baselineTask,
      resources: [resource],
      resource_id: String(baselineTask.resource_id),
      resource_data: [resource],
      // cost: estimationsModule.calculateCostChildTask(baselineTask) || 0,
      // actual_cost: estimationsModule.calculateActualCostChildTask(baselineTask) || 0,
      cost: this.tasksCostsMap.get(baselineTask.gantt_id)[baselineTask.id].cost,
      actual_cost: this.tasksCostsMap.get(baselineTask.gantt_id)[baselineTask.id].actual_cost,
      cost_range_start: baselineTask.start_date,
      cost_range_end: baselineTask.end_date,
      logsData: taskLogsData,
    };
  }

  prepareTableTasks() {
    const tableTasks = [];
    const reportTasksByAssignee = _.pickBy(this.reportTasks, (v, k) => !k.startsWith('baseline-'));

    Object.entries(reportTasksByAssignee).forEach(([key, data]) => {
      const resourceId = key.split('-')[0].split(':')[1];

      data.tasks.forEach(task => {
        const baselineData = this.reportTasks[`baseline-${resourceId}-${task.id}`];
        const baselineTaskCost = baselineData?.tasks[0]?.cost || 0;
        let variance = 0;

        if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED) {
          variance = task.cost - task.actual_cost;
        }

        if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE) {
          variance = baselineTaskCost - task.actual_cost;
        }

        if (this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
          variance = baselineTaskCost - task.cost;
        }

        const resource = this.projectResourcesByIdMap.get(task.gantt_id)[resourceId];
        const projectData = this.projectDataMap.get(task.gantt_id);

        const preparedTask = {
          id: task.id,
          task: { id: task.id, name: task.task_data.text },
          project: { id: task.gantt_id, name: projectData.name, color: ColorCycler.getColor(task.gantt_id) },
          assignee: resource,
          baseline_budget: baselineTaskCost,
          budget: task.cost,
          actual_cost: task.actual_cost,
          variance,
        };

        tableTasks.push(preparedTask);
      });
    });

    return tableTasks;
  }

  initReportData() {
    const tableTasks = this.prepareTableTasks();

    app.trigger('onAfterResourcesCostsCalculation', {
      table: tableTasks,
    });
  }
}

const budgetByResource = new BudgetByResourceReport();

export default budgetByResource;
